import { useCallback, useEffect, useState } from "react";
import { CalendarClock, EyeOff, Plus, RotateCcw, Trash2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CategoryBadge } from "../components/CategoryBadge";
import { cadenceLabels, formatDate, formatSEK0 } from "../lib/format";
import type { RecurringItem, RecurringOverview } from "../types";

export function RecurringPage() {
  const [data, setData] = useState<RecurringOverview | null>(null);
  const [showIgnored, setShowIgnored] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addName, setAddName] = useState("");
  const [addAmount, setAddAmount] = useState("");
  const [addCadence, setAddCadence] = useState("monthly");
  const [addDay, setAddDay] = useState("");

  const refresh = useCallback(() => {
    fetch("/api/recurring")
      .then((r) => r.json())
      .then(setData)
      .catch(() => {});
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function handleIgnore(key: string, ignored: boolean) {
    await fetch("/api/recurring/ignore", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ key, ignored }),
    });
    refresh();
  }

  async function handleDeleteManual(key: string) {
    const id = key.replace(/^manual:/, "");
    await fetch(`/api/recurring/manual/${id}`, { method: "DELETE" });
    refresh();
  }

  async function handleAdd() {
    const amount = Number(addAmount.replace(/\s/g, "").replace(",", "."));
    if (!addName.trim() || !Number.isFinite(amount) || amount <= 0) return;
    await fetch("/api/recurring", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: addName.trim(),
        amount,
        cadence: addCadence,
        dayOfMonth: addDay ? Number(addDay) : undefined,
      }),
    });
    setAddName("");
    setAddAmount("");
    setAddDay("");
    setShowAddForm(false);
    refresh();
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  const visible = data.items.filter((i) => (showIgnored ? true : !i.ignored));
  const active = visible.filter((i) => i.active && !i.ignored);
  const inactive = visible.filter((i) => !i.active || i.ignored);
  const yearlyTotal = data.activeMonthlyTotal * 12;

  function renderItem(item: RecurringItem) {
    return (
      <div
        key={item.key}
        className={`group flex items-center gap-3 rounded-lg border p-3 ${
          item.ignored || !item.active ? "opacity-60" : ""
        }`}
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="truncate font-medium" title={item.name}>
              {item.name}
            </span>
            <CategoryBadge category={item.category} subcategory={item.subcategory} />
            {item.source === "manual" && (
              <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                Manuell
              </span>
            )}
            {!item.active && item.source === "detected" && (
              <span className="rounded-full bg-warning/15 px-2 py-0.5 text-[10px] font-medium text-warning">
                Möjligen avslutad
              </span>
            )}
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {cadenceLabels[item.cadence]}
            {item.occurrences ? ` · ${item.occurrences} betalningar` : ""}
            {item.nextDate && item.active ? ` · nästa ca ${formatDate(item.nextDate)}` : ""}
          </div>
        </div>

        <div className="text-right">
          <div className="font-semibold tabular-nums">{formatSEK0(item.amount)}</div>
          {item.cadence !== "monthly" && (
            <div className="text-xs text-muted-foreground tabular-nums">
              ~{formatSEK0(item.monthlyAmount)}/mån
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center">
          {item.source === "manual" ? (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100 max-lg:opacity-60"
              onClick={() => void handleDeleteManual(item.key)}
              aria-label={`Ta bort ${item.name}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          ) : item.ignored ? (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground"
              onClick={() => void handleIgnore(item.key, false)}
              title="Visa igen"
              aria-label={`Visa ${item.name} igen`}
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 max-lg:opacity-60"
              onClick={() => void handleIgnore(item.key, true)}
              title="Dölj — inte en fast kostnad"
              aria-label={`Dölj ${item.name}`}
            >
              <EyeOff className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 lg:space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-2xl font-bold tracking-tight">Fasta kostnader</h2>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={showIgnored}
            onChange={(e) => setShowIgnored(e.target.checked)}
            className="h-4 w-4"
          />
          Visa dolda
        </label>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Per månad</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold tabular-nums">
              {formatSEK0(data.activeMonthlyTotal)}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {data.activeCount} aktiva fasta kostnader
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Per år</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold tabular-nums">{formatSEK0(yearlyTotal)}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Vad de fasta kostnaderna kostar på ett år
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-muted-foreground" />
            Återkommande kostnader
          </CardTitle>
          <CardDescription>
            Hittas automatiskt i transaktionerna (vecko-, månads-, kvartals- och
            årsvisa mönster). Dölj det som inte är en riktig fast kostnad och lägg
            till sådant som inte syns i kontoutdragen.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {!showAddForm ? (
            <Button variant="outline" size="sm" onClick={() => setShowAddForm(true)}>
              <Plus className="mr-2 h-4 w-4" /> Lägg till manuellt
            </Button>
          ) : (
            <div className="grid gap-2 rounded-lg border p-3 sm:grid-cols-2 lg:grid-cols-5">
              <Input
                placeholder="Namn (t.ex. Hemförsäkring)"
                value={addName}
                onChange={(e) => setAddName(e.target.value)}
                autoFocus
              />
              <Input
                placeholder="Belopp (kr)"
                inputMode="decimal"
                value={addAmount}
                onChange={(e) => setAddAmount(e.target.value)}
              />
              <Select value={addCadence} onValueChange={setAddCadence}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(cadenceLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                placeholder="Dag i månaden (1–28)"
                inputMode="numeric"
                value={addDay}
                onChange={(e) => setAddDay(e.target.value)}
              />
              <div className="flex gap-2">
                <Button onClick={() => void handleAdd()} disabled={!addName.trim() || !addAmount}>
                  Spara
                </Button>
                <Button variant="ghost" onClick={() => setShowAddForm(false)}>
                  Avbryt
                </Button>
              </div>
            </div>
          )}

          {active.length === 0 && inactive.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Inga återkommande kostnader hittade ännu. Ladda upp fler månader av
              kontoutdrag så upptäcks mönstren automatiskt.
            </p>
          ) : (
            <div className="space-y-2">
              {active.map(renderItem)}
              {inactive.length > 0 && (
                <>
                  <p className="pt-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Inaktiva / dolda
                  </p>
                  {inactive.map(renderItem)}
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
